import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigation } from "react-router-dom";
import { useIsFetching } from "@tanstack/react-query";

// Top-of-viewport progress bar that lights up while the router is resolving a
// lazy chunk / loader OR while React Query has any in-flight fetch. Mirrors the
// nprogress UX: ramps to ~85% on activity, snaps to 100% on settle, fades out.
export const NavigationProgress = () => {
  const navigation = useNavigation();
  const fetchingCount = useIsFetching();
  const isActive = navigation.state !== "idle" || fetchingCount > 0;

  const [visible, setVisible] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let showTimer: number | undefined;
    let hideTimer: number | undefined;

    if (isActive) {
      if (!visible) {
        // Debounce so sub-80ms fetches don't flash the bar.
        showTimer = window.setTimeout(() => {
          setDone(false);
          setVisible(true);
        }, 80);
      } else {
        // Activity resumed mid-finish — keep climbing instead of completing.
        setDone(false);
      }
    } else if (visible) {
      setDone(true);
      hideTimer = window.setTimeout(() => {
        setVisible(false);
        setDone(false);
      }, 350);
    }

    return () => {
      if (showTimer) clearTimeout(showTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [isActive, visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          aria-hidden
          className="fixed top-0 left-0 right-0 h-[3px] z-50 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <motion.div
            className="h-full bg-gradient-to-r from-[#3C3295] via-[#716DEE] to-[#00A49B] shadow-[0_0_8px_rgba(113,109,238,0.6)]"
            initial={{ width: "0%" }}
            animate={{ width: done ? "100%" : "85%" }}
            transition={{
              duration: done ? 0.2 : 1.2,
              ease: done ? "easeOut" : [0.1, 0.5, 0.2, 1],
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
